"use client";

import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, useEffect, startTransition } from "react";
import { useSearchParams, useRouter, useParams } from "next/navigation";
import ProductCard from "@/components/molecules/product-card";
import ProductCardSkeletonLoader from "@/components/atoms/skeleton-loaders/product-card-skeleton-loader";
import PaginationControls from "@/components/molecules/pagination-controls";
import Footer from "@/components/molecules/landing-section-two/footer";
import { Button } from "@/components/ui/button";
import ProductFilters from "@/components/molecules/product-filters";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ShopBreadcrumbs from "@/components/molecules/shop-breadcrumbs";
import { formatSlug } from "@/lib/utils/format-slug";
import CTASection from "@/components/molecules/landing-section-two/discover";
import EmptySearchIcon from "@/components/atoms/dynamic-icons/empty-search";
import Link from "next/link";

export default function CategoryPage() {
  const [isLoading, setIsLoading] = useState(false);
  console.log("isLoading", setIsLoading);
  const [checkedCategories, setCheckedCategories] = useState<{ [key: string]: boolean }>({});
  const [checkedBrands, setCheckedBrands] = useState<{ [key: string]: boolean }>({});
  const [priceRange, setPriceRange] = useState<[number, number]>([5000, 700000]);
  const searchParams = useSearchParams();
  const router = useRouter();
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);

  const categorySlug = params.slug as string;
  const categoryName = formatSlug(categorySlug);
  const pageFromQuery = Number(searchParams.get("page")) || 1;

  // Mock categories data - in real app, fetch this from API
  const allCategories = [
    { id: 1, name: "Furniture", slug: "furniture", count: 24 },
    { id: 2, name: "Electronics", slug: "electronics", count: 45 },
    { id: 3, name: "Clothing", slug: "clothing", count: 18 },
    { id: 4, name: "Books", slug: "books", count: 12 },
    { id: 5, name: "Beauty", slug: "beauty", count: 8 },
    { id: 6, name: "Fashion", slug: "fashion", count: 32 },
    { id: 7, name: "Sports", slug: "sports", count: 16 },
    { id: 8, name: "Toys", slug: "toys", count: 10 },
  ];

  // Find current category ID by slug
  const currentCategory = allCategories.find((cat) => cat.slug === categorySlug);
  const currentCategoryId = currentCategory?.id;

  // Sync filters with URL on mount
  useEffect(() => {
    const categoryParams = searchParams.get("category")?.split(",").filter(Boolean) || [];
    const brandParams = searchParams.get("brand")?.split(",").filter(Boolean) || [];
    const minPrice = Number(searchParams.get("minPrice")) || 5000;
    const maxPrice = Number(searchParams.get("maxPrice")) || 700000;

    const newCheckedCategories: { [key: string]: boolean } = {};
    categoryParams.forEach((id) => {
      newCheckedCategories[id] = true;
    });

    const newCheckedBrands: { [key: string]: boolean } = {};
    brandParams.forEach((id) => {
      newCheckedBrands[id] = true;
    });

    startTransition(() => {
      setCheckedCategories(newCheckedCategories);
      setCheckedBrands(newCheckedBrands);
      setPriceRange([minPrice, maxPrice]);
    });
  }, [searchParams]);

  // Update URL when filters change
  const updateURL = (updates: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams.toString());

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    router.push(`/category/${categorySlug}?${params.toString()}`, { scroll: false });
  };

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    const newCheckedCategories = {
      ...checkedCategories,
      [categoryId]: checked,
    };
    setCheckedCategories(newCheckedCategories);

    const selectedCategories = Object.entries(newCheckedCategories)
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => id);

    updateURL({
      category: selectedCategories.length > 0 ? selectedCategories.join(",") : undefined,
    });
  };

  const handleBrandChange = (brandId: number, checked: boolean) => {
    const newCheckedBrands = {
      ...checkedBrands,
      [brandId]: checked,
    };
    setCheckedBrands(newCheckedBrands);

    const selectedBrands = Object.entries(newCheckedBrands)
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => id);

    updateURL({
      brand: selectedBrands.length > 0 ? selectedBrands.join(",") : undefined,
    });
  };

  const handlePriceRangeChange = (range: [number, number]) => {
    setPriceRange(range);
    updateURL({
      minPrice: range[0].toString(),
      maxPrice: range[1].toString(),
    });
  };

  const meta = useMemo(() => {
    // Mocked meta; replace when connecting backend
    const totalItems = 100;
    const pageSize = 9;
    const totalPages = 10;
    const currentPage = Math.max(1, Math.min(pageFromQuery, totalPages));
    return {
      totalItems,
      pageSize,
      totalPages,
      currentPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [pageFromQuery]);

  const categories = allCategories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    count: cat.count,
  }));

  const brands = [
    { id: 1, name: "Oraimo", count: 15 },
    { id: 2, name: "Samsung", count: 45 },
    { id: 3, name: "Apple", count: 32 },
    { id: 4, name: "Nike", count: 28 },
    { id: 5, name: "Adidas", count: 22 },
    { id: 6, name: "Puma", count: 18 },
    { id: 7, name: "Reebok", count: 12 },
    { id: 8, name: "Under Armour", count: 9 },
    { id: 9, name: "New Balance", count: 14 },
    { id: 10, name: "Converse", count: 20 },
    { id: 11, name: "Vans", count: 16 },
    { id: 12, name: "Fila", count: 11 },
    { id: 13, name: "Skechers", count: 13 },
  ];

  const products = [
    {
      id: 1,
      title: "Product 1",
      price: 100000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 2,
      title: "Product 2",
      price: 200000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 3,
      title: "Product 3",
      price: 300000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 4,
      title: "Product 4",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 5,
      title: "Product 5",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 6,
      title: "Product 6",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 7,
      title: "Product 7",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 8,
      title: "Product 8",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
    {
      id: 9,
      title: "Product 9",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      noOfReviews: 100,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
    },
  ];

  // Check if filters are applied
  const hasFiltersApplied =
    Object.values(checkedCategories).some((checked) => checked) ||
    Object.values(checkedBrands).some((checked) => checked) ||
    priceRange[0] !== 5000 ||
    priceRange[1] !== 700000;

  const hasProducts = products.length > 0;

  return (
    <div className='flex min-h-svh w-full flex-col'>
      <div className='flex w-full gap-4 px-0 xl:px-5 xl:py-6'>
        <aside className='hidden w-1/5 shrink-0 xl:block'>
          <ProductFilters
            categories={categories}
            brands={brands}
            checkedCategories={checkedCategories}
            checkedBrands={checkedBrands}
            onCategoryChange={handleCategoryChange}
            onBrandChange={handleBrandChange}
            priceRange={priceRange}
            onPriceRangeChange={handlePriceRangeChange}
            hiddenCategoryIds={currentCategoryId ? [currentCategoryId] : []}
          />
        </aside>
        <main className='flex-1'>
          <div className='px-3 py-3 xl:mb-6 xl:px-0 xl:py-0'>
            <ShopBreadcrumbs currentPage={categoryName} className='mb-3' />
            <h1 className='text-text-title text-2xl font-semibold md:text-3xl'>{categoryName}</h1>
          </div>

          <div className='mb-2 px-3 py-3 xl:hidden'>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant='outline' className='h-10 rounded-lg border-[1.4px] px-3'>
                  <AdjustmentsHorizontalIcon className='text-icon-title size-4 stroke-[1.5px]' />
                  <span className='text-text-title text-sm'>Filters</span>
                </Button>
              </SheetTrigger>
              <SheetContent side='left' className='py-6'>
                <div className='flex w-full items-center justify-between px-3'>
                  <SheetClose asChild>
                    <XMarkIcon className='text-icon-title size-5 stroke-2' />
                  </SheetClose>
                  <Button onClick={() => setIsOpen(false)}>Apply Filter</Button>
                </div>
                <div className='overflow-y-auto px-3'>
                  <ProductFilters
                    categories={categories}
                    brands={brands}
                    checkedCategories={checkedCategories}
                    checkedBrands={checkedBrands}
                    onCategoryChange={handleCategoryChange}
                    onBrandChange={handleBrandChange}
                    priceRange={priceRange}
                    onPriceRangeChange={handlePriceRangeChange}
                    hiddenCategoryIds={currentCategoryId ? [currentCategoryId] : []}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {!hasProducts ? (
            <div>
              <EmptySearchIcon className='mx-auto mb-7' />
              <p className='text-btn-primary text-center text-2xl font-extrabold'>
                {hasFiltersApplied ? "No products match your filters" : "No products found"}
              </p>
              <p className='text-btn-primary mb-7 text-center text-base'>
                {hasFiltersApplied
                  ? "Try adjusting your filters or browse other categories"
                  : "No products found in this category"}
              </p>
              <Link href='/shop' className='mx-auto flex w-fit'>
                <Button variant='outline' className='text-sm font-normal tracking-[1.4px] uppercase'>
                  Browse Products
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className='border-border-primmary/30 relative grid grid-cols-2 gap-4 border-y bg-[#F9FAFB] px-3 py-6 md:grid-cols-3 md:gap-8 md:px-8 xl:border-0 xl:bg-transparent xl:py-0 dark:bg-transparent'>
                {isLoading
                  ? [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => <ProductCardSkeletonLoader size='md' key={item} />)
                  : products.map((product) => <ProductCard size='md' key={product.id} {...product} />)}
              </div>
              {!isLoading && <PaginationControls meta={meta} className='mt-12' />}
            </>
          )}
        </main>
      </div>
      <CTASection />
      <Footer />
    </div>
  );
}
